/**
 * Renders one appetite answer inside Connor's chat.
 *
 * The engine returns far more than fits in a 360px panel — a class can have 48
 * markets — so the job here is deciding what an agent needs in the first two
 * seconds and what they should have to ask for.
 *
 * Shown immediately: the class we matched, how many markets, and the top six with
 * their underwriting note. The note ("E&S", "Not TX", "Non-Habitational") is the
 * team's own caveat and is the difference between a name and a decision, so it is
 * never behind a toggle.
 *
 * Behind a click: the rest of the markets, the evidence for a given carrier, and
 * anything excluded on state grounds. Excluded markets are *shown*, not silently
 * dropped — "we can't place that in FL" is an answer, and an agent who can't see
 * it assumes we simply don't have the market.
 */
import { useState, type ReactNode } from 'react';
import type { AppetiteAnswer, CarrierHit, ClassResult } from '@commpass/shared';

const TOP_N = 6;

function ConfidenceDot({ hit }: { hit: CarrierHit }) {
  return (
    <span
      className={`ap-dot is-${hit.confidence.toLowerCase()}`}
      title={hit.signals.join(' · ')}
      aria-label={`${hit.confidence} confidence`}
    />
  );
}

function CarrierRow({ hit, state }: { hit: CarrierHit; state: string | null }) {
  const [showWhy, setShowWhy] = useState(false);
  return (
    <li className="ap-carrier">
      <button className="ap-carrier-head" onClick={() => setShowWhy((v) => !v)} aria-expanded={showWhy}>
        <ConfidenceDot hit={hit} />
        <span className="ap-carrier-name">{hit.name}</span>
        {/* An underwriter's caveat, verbatim. Never paraphrased or hidden. */}
        {hit.note && <span className="ap-note">{hit.note}</span>}
      </button>
      {showWhy && (
        <div className="ap-why">
          <ul>
            {hit.signals.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          {hit.evidence?.snippet && (
            <blockquote className="ap-quote">
              {hit.evidence.snippet}
              <cite>{hit.evidence.file}</cite>
            </blockquote>
          )}
          {/* Only claim a state fact when there is one. Most wholesalers have no
              state data at all, and "appointed nowhere" would be a lie. */}
          {hit.stateKnown && hit.states.length > 0 && (
            <div className="ap-states">
              {state && hit.states.includes(state)
                ? `Appointed in ${state}.`
                : `Appointed in ${hit.states.length} states.`}
            </div>
          )}
          {hit.lines.length > 0 && <div className="ap-states">{hit.lines.join(' · ')}</div>}
        </div>
      )}
    </li>
  );
}

function ClassBlock({ result, state }: { result: ClassResult; state: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const shown = expanded ? result.carriers : result.carriers.slice(0, TOP_N);
  const rest = result.carriers.length - shown.length;

  return (
    <div className="ap-class">
      <div className="ap-class-head">
        <strong>{result.class}</strong>
        <span className="ap-count">
          {result.carriers.length} market{result.carriers.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="ap-why-class">{result.why}</div>

      <ul className="ap-carriers">
        {shown.map((c) => (
          <CarrierRow key={c.name} hit={c} state={state} />
        ))}
      </ul>

      {rest > 0 && (
        <button className="ap-more" onClick={() => setExpanded(true)}>
          Show {rest} more
        </button>
      )}

      {result.excludedForState.length > 0 && (
        <div className="ap-excluded">
          <button className="ap-more" onClick={() => setShowExcluded((v) => !v)} aria-expanded={showExcluded}>
            {showExcluded ? 'Hide' : `${result.excludedForState.length} not available in ${state}`}
          </button>
          {showExcluded && (
            <ul>
              {result.excludedForState.map((e) => (
                <li key={e.name}>
                  {e.name} <span className="ap-note-plain">{e.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bold the words that earned a citation.
 *
 * ⚠️ ROOTS, NOT WHOLE WORDS (Josh, 2026-08-30): asking about a "roofing
 * contractor" should light up "Roofing", "Roofer" and "Roofers" in a carrier's
 * guide, not just an exact repeat of what was typed. So each matched term is
 * stemmed back to a root and re-matched with the usual endings. Without this an
 * agent reads four lines of carrier boilerplate hunting for the reason the
 * document came back at all.
 *
 * Roots shorter than four characters are skipped — "gas" would bold half a page.
 */
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function highlight(text: string, terms: string[]): ReactNode {
  const roots = [...new Set((terms ?? []).map((t) => t.toLowerCase().replace(/(ings|ing|ers|er|es|s)$/, '')))]
    .filter((r) => r.length >= 4)
    .sort((a, b) => b.length - a.length);
  if (!roots.length) return text;

  // ⚠️ String.raw, not a plain template: `\b` in a template literal is a
  // BACKSPACE character, not a word boundary, and the regex would silently
  // match nothing.
  const re = new RegExp(String.raw`\b(` + roots.map(escapeRe).join('|') + String.raw`)(ings|ing|ers|er|es|s)?\b`, 'gi');
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    out.push(
      <mark key={`${i}-${m[0]}`} className="ap-hit">
        {m[0]}
      </mark>,
    );
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function AppetiteAnswerCard({ answer }: { answer: AppetiteAnswer }) {
  const [showDocs, setShowDocs] = useState(false);

  if (answer.noClassMatch && !answer.specialistMarkets.length) {
    return (
      <div className="ap-card">
        <div className="ap-empty">
          I couldn&apos;t match that to a class I hold markets for. Try naming the operation
          plainly — &ldquo;roofing contractor&rdquo;, &ldquo;12-unit apartment building&rdquo;,
          &ldquo;auto repair shop&rdquo;.
        </div>
      </div>
    );
  }

  return (
    <div className="ap-card">
      <div className="ap-headline">
        {answer.totalCarriers} appointed market{answer.totalCarriers === 1 ? '' : 's'} to look at
        {answer.state ? ` in ${answer.state}` : ''}
      </div>

      {answer.classes.map((c) => (
        <ClassBlock key={c.class} result={c} state={answer.state} />
      ))}

      {/* The "you already have the specialist and didn't know it" catch. Surfaced
          separately because it holds even when no class matched at all. */}
      {answer.specialistMarkets.length > 0 && (
        <div className="ap-class">
          <div className="ap-class-head">
            <strong>Specialists for this risk</strong>
          </div>
          <ul className="ap-carriers">
            {answer.specialistMarkets.map((s) => (
              <li key={s.name} className="ap-carrier">
                <div className="ap-carrier-head as-plain">
                  <span className="ap-dot is-high" />
                  <span className="ap-carrier-name">{s.name}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {answer.documents.length > 0 && (
        <div className="ap-docs">
          <button className="ap-more" onClick={() => setShowDocs((v) => !v)} aria-expanded={showDocs}>
            {showDocs ? 'Hide sources' : `Where this came from (${answer.documents.length})`}
          </button>
          {showDocs && (
            <ul>
              {answer.documents.map((d) => (
                <li key={d.file}>
                  <div className="ap-doc-name">{d.file}</div>
                  {d.snippets[0] && <div className="ap-doc-snip">{highlight(d.snippets[0], d.terms)}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ⚠️ ABOVE the guides on purpose, and the only section here that is
          evidence rather than intent. Everything else says what a carrier
          claims to want; this says what they accepted our business on. An
          agent who sees "9 policies, 6 accounts" stops reading the rest. */}
      {answer.ownBook && answer.ownBook.hits.length > 0 && (
        <div className="ap-book">
          <div className="ap-book-head">
            We have placed business like this
            <span>Our own book — what these markets actually wrote, not what a guide says.</span>
          </div>
          <ul>
            {answer.ownBook.hits.map((h) => (
              <li key={h.name}>
                <b>{h.name}</b>
                <span className="ap-book-n">
                  {h.policies} polic{h.policies === 1 ? 'y' : 'ies'} · {h.customers} account
                  {h.customers === 1 ? '' : 's'}
                  {answer.state && h.states.includes(answer.state) ? ` · incl. ${answer.state}` : ''}
                </span>
                {/* Whose paper it went on — a different question, and one an
                    agent asks the moment they see a wholesaler's name. */}
                {h.paper.length > 0 && <span className="ap-book-paper">on {h.paper.join(', ')}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ⚠️ VISIBLY WEAKER THAN EVERYTHING ABOVE IT. Only shown when our guides
          came up thin, framed as "somewhere to start" rather than an answer,
          and every claim carries its source so an agent can judge it. A public
          page saying a carrier writes a class says nothing about whether they
          write it in this state, through our appointment. */}
      {answer.web && answer.web.notes.length > 0 && (
        <div className="ap-web">
          <div className="ap-web-head">
            From the open web — not our appetite guides
            <span>Our own guides had little on this. Treat these as a starting point and verify.</span>
          </div>
          <ul>
            {answer.web.notes.map((n) => (
              <li key={n.url}>
                {n.carrier && <b>{n.carrier}</b>}
                <span>{n.text}</span>
                <a href={n.url} target="_blank" rel="noopener noreferrer">
                  {n.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ap-foot">
        {answer.overall.level} confidence · {answer.classifier} · data as of{' '}
        {new Date(answer.dataAsOf).toLocaleDateString()}
      </div>
    </div>
  );
}
